import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { adminStats, adminListOrders, getSettings } from "@/lib/api";
import { statusColor } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import SalesAnalytics from "@/components/SalesAnalytics";
import { Package, ShoppingBag, Users, Clock, IndianRupee, TrendingUp, MessageCircle, AlertTriangle } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-md p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <div className="w-9 h-9 rounded-md bg-[#EFEBE2] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#2D5A27]" />
      </div>
    </div>
    <p className="font-heading font-bold text-2xl text-[#1E293B]">{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const AdminDashboardHome = () => {
  const { t, lang, adminToken } = useApp();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([adminStats(adminToken), adminListOrders(adminToken), getSettings()])
      .then(([s, o, cfg]) => { setStats(s); setRecent(o.slice(0, 5)); setSettings(cfg); })
      .finally(() => setLoading(false));
  }, [adminToken]);

  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#1E293B] mb-1">{t("dashboard")}</h1>
      <p className="text-sm text-slate-500 mb-6">Overview of your Krishidut store</p>

      {settings && !settings.shop_whatsapp && (
        <Link
          to="/admin/settings"
          data-testid="whatsapp-setup-banner"
          className="mb-6 p-4 rounded-md bg-amber-50 border border-amber-300 flex items-center gap-3 hover:bg-amber-100 transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-semibold text-sm text-amber-900">Enable WhatsApp order alerts</p>
            <p className="text-xs text-amber-800">Set your WhatsApp number in Settings so farmers can instantly notify you when they place an order.</p>
          </div>
          <span className="text-sm font-semibold text-amber-900">Set now →</span>
        </Link>
      )}

      {settings && settings.shop_whatsapp && (
        <div className="mb-6 p-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-center gap-3" data-testid="whatsapp-configured-banner">
          <MessageCircle className="w-5 h-5 text-emerald-700 shrink-0" />
          <p className="text-sm text-emerald-900">
            WhatsApp order alerts active on <span className="font-mono font-semibold">+{settings.shop_whatsapp}</span>
          </p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 bg-white border rounded-md animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={ShoppingBag} label={t("totalOrders")} value={stats.total_orders} testId="stat-total-orders" />
            <StatCard icon={Clock} label={t("pendingOrders")} value={stats.pending_orders} hint={lang === "hi" ? "कार्रवाई आवश्यक" : "Needs attention"} testId="stat-pending-orders" />
            <StatCard icon={Package} label={t("totalProducts")} value={stats.total_products} testId="stat-products" />
            <StatCard icon={Users} label={t("totalFarmers")} value={stats.total_farmers} testId="stat-farmers" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <StatCard icon={IndianRupee} label={t("revenue")} value={`₹${stats.revenue.toFixed(0)}`} hint={lang === "hi" ? "पहुँचाए गए ऑर्डर से" : "From delivered orders"} testId="stat-revenue" />
            <StatCard icon={TrendingUp} label={t("delivered_orders")} value={stats.delivered_orders} testId="stat-delivered" />
          </div>

          <SalesAnalytics />

          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-lg text-[#1E293B]">Recent Orders</h2>
              <Link to="/admin/orders" className="text-sm text-[#2D5A27] font-semibold" data-testid="view-all-orders-link">View all →</Link>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
              {recent.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">{t("noOrders")}</div>
              ) : (
                <div className="divide-y divide-[#E2E8F0]">
                  {recent.map((o) => (
                    <div key={o.id} className="p-4 flex items-center justify-between gap-4" data-testid={`recent-order-${o.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-mono font-semibold text-sm text-[#1E293B]">{o.order_number}</p>
                        <p className="text-xs text-slate-500 truncate">{o.delivery_name} · {o.delivery_mobile}</p>
                      </div>
                      <Badge variant="outline" className={`${statusColor[o.status]} shrink-0`}>{t(o.status)}</Badge>
                      <p className="font-heading font-bold text-[#2D5A27] shrink-0">₹{o.total.toFixed(0)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboardHome;
